<template>
  <div class="relative">
    <div class="flex w-full flex-col rounded-md bg-default p-5">
      <div
        class="flex cursor-pointer"
        :class="{
          'mb-5': isContainerOpen,
          'mb-0': !enableCollapse || !isContainerOpen,
        }"
        @click="isContainerOpen = !isContainerOpen"
      >
        <h4 v-if="title" class="text-title-color2">
          {{ props.title }}
        </h4>
        <span class="relative">
          <ToolTipOnHover
            v-if="titleTooltip"
            :tooltip-text="titleTooltip"
            tooltip-container-classes="absolute bottom-0 left-0.5"
            :icon-size="24"
          />
        </span>
      </div>

      <div v-if="containerVisible" class="flex flex-col gap-5">
        <slot />
      </div>
      <div v-if="containerVisible" class="mt-3 flex h-[58px] w-full items-end">
        <ButtonEl
          v-if="!readOnly"
          :disabled="!applySelectionEnabled"
          :text="buttonText"
          icon="assignment_turned_in"
          :mdi="true"
          class="mr-5 h-10 text-nowrap"
          icon-type="round"
          data-test="scenario-section-submit-button"
          @click="
            granularSelectionEnabled && enableShowItems
              ? $emit('granularSelectionButtonClick')
              : handleAllSelectionButtonClick()
          "
        />

        <div
          v-if="availableItems"
          class="flex w-full flex-col gap-2.5 rounded-[4px] bg-default text-neutral"
        >
          <div class="flex items-end justify-between gap-2.5">
            <VTooltip
              v-if="!granularSelectionEnabled"
              activator="parent"
              location="top"
              max-width="500px"
            >
              <p>
                {{ buttonNaming }} können eingeblendet werden, sobald verfügbare
                Eingaben für alle {{ buttonNaming }} angewandt wurden.
              </p>
            </VTooltip>
            <div
              v-if="!readOnly"
              class="button-1 flex h-10 w-full items-center px-2.5 py-2"
              :class="{
                'cursor-pointer bg-button-secondary-default-neutral text-title-neutral':
                  granularSelectionEnabled,
                'cursor-not-allowed bg-button-secondary-disabled-neutral text-disabled-neutral':
                  !granularSelectionEnabled,
                '!w-[240px]': enableShowItems && showFilters,
              }"
              @click="showItems"
            >
              <IconWrapper
                :icon="`${enableShowItems ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}`"
              />
              <span class="ml-1.5">{{
                `${aggregateNaming[aggregateLevel].plural} ${enableShowItems ? 'ausblenden' : 'einblenden'}`
              }}</span>
            </div>
            <div
              :class="{ 'ml-auto': readOnly }"
              class="flex items-end gap-2.5"
            >
              <div v-if="showFilters" class="flex items-end gap-2.5">
                <DropDownMultiple
                  v-model="filters.countiesFilter"
                  label="Landkreise"
                  item-type-all="Landkreise"
                  all-selected-label="Alle"
                  class="w-36"
                  :items="availableItems.counties"
                  data-key="county_name"
                  value-key="county_id"
                  placeholder="Bitte auswählen"
                  data-test="county-filter"
                />
                <DropDownMultiple
                  v-if="showMunicipalityFilter"
                  v-model="filters.municipalitiesFilter"
                  label="Gemeinden"
                  item-type-all="Gemeinden"
                  all-selected-label="Alle"
                  class="w-36"
                  :items="totalMunicipalities"
                  data-key="municipality_name"
                  value-key="municipality_id"
                  placeholder="Bitte auswählen"
                  data-test="municipality-filter"
                />
                <div class="relative">
                  <InputEl
                    label="Textfilter"
                    :model-value="filters.textFilter"
                    :placeholder="`Nach ${aggregateNaming[aggregateLevel].singular}`"
                    :suffix-icon="true"
                    suffix="ZoomIcon"
                    class="w-36"
                    @update:model-value="filters.textFilter = $event"
                  />
                  <IconWrapper
                    v-if="filters.textFilter"
                    icon="close"
                    class="absolute right-[34px] top-[38px] translate-y-[-50%] cursor-pointer bg-active-area"
                    @click="filters.textFilter = ''"
                  />
                </div>
                <ButtonEl
                  v-if="showUnsetFilter"
                  :text="`${aggregateNaming[aggregateLevel].plural} ohne Wert`"
                  :variant="filters.unsetFilter ? 'primary' : 'secondary'"
                  :icon="
                    filters.unsetFilter
                      ? 'radio_button_checked'
                      : 'radio_button_unchecked'
                  "
                  class="whitespace-nowrap"
                  :mdi="true"
                  @click="filters.unsetFilter = !filters.unsetFilter"
                />
              </div>
              <ButtonEl
                v-if="enableShowItems"
                :mdi="true"
                icon="filter_alt"
                icon-type="round"
                :variant="showFilters ? 'secondary' : 'primary'"
                @click="showFilters = !showFilters"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="enableShowItems && availableItems && containerVisible"
        class="mt-5 flex flex-col gap-2.5"
      >
        <div
          class="standard-elevation-0-dark overflow-hidden rounded-md pl-[1px]"
          data-test="details-table"
          @scroll.stop
        >
          <VuetifyDataTable
            :headers="[...baseHeaders, ...headers]"
            :item-data="filteredItems"
            :item-value="keyNameForCheckbox"
            :show-select="!readOnly"
            :show-expand="false"
            :select-all-checkboxes="!readOnly"
            @selected-rows="emit('itemClick', $event)"
          />
        </div>
      </div>
    </div>
    <IconWrapper
      v-if="enableCollapse"
      :icon="isContainerOpen ? 'unfold_less' : 'unfold_more'"
      fill="text-title-neutral"
      type="round"
      class="absolute right-5 top-5 cursor-pointer"
      @click="isContainerOpen = !isContainerOpen"
    />
  </div>
</template>

<script setup>
import ButtonEl from '@/components/storybook/src/stories/button/ButtonEl.vue';
import InputEl from '@/components/storybook/src/stories/input/InputEl.vue';
import VuetifyDataTable from '@/components/storybook/src/stories/vuetifyDataTable/VuetifyDataTable.vue';
import DropDownMultiple from '@/components/storybook/src/stories/dropDownMultiple/DropDownMultiple.vue';
import IconWrapper from '@/components/storybook/src/stories/IconWrapper/IconWrapper.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { toObject } from '@/apps/scenarios/scenario-helpers';
import { useStore } from 'vuex';
import ToolTipOnHover from '@/components/storybook/src/stories/toolTipOnHover/ToolTipOnHover.vue';

const aggregateNaming = {
  district: {
    singular: 'Stadtteil',
    plural: 'Stadtteile',
  },
  municipality: {
    singular: 'Gemeinde',
    plural: 'Gemeinden',
  },
};

const baseHeaders = computed(() => {
  const baseHeaders = [
    {
      title: 'Gemeinde',
      key: 'municipality_name',
      width: '150px',
      align: 'left',
    },
  ];

  if (!props.readOnly) {
    baseHeaders.unshift({
      title: '',
      key: 'show_details',
      align: 'center',
      sortable: false,
      width: '100px',
    });
  }

  return baseHeaders;
});

const props = defineProps({
  applySelectionEnabled: Boolean,
  granularSelectionEnabled: Boolean,
  buttonNaming: {
    default: 'Stadtteile',
    type: String,
  },
  showUnsetFilter: {
    default: true,
    type: Boolean,
  },
  showMunicipalityFilter: {
    default: true,
    type: Boolean,
  },
  availableItems: Object,
  filteredItems: Array,
  selectedItems: Array,
  headers: Array,
  keyNameForCheckbox: {
    default: 'district',
    type: String,
  },
  aggregateLevel: {
    default: 'district',
    type: String,
  },
  readOnly: {
    default: false,
    type: Boolean,
  },
  title: {
    default: '',
    type: String,
  },
  titleTooltip: {
    default: '',
    type: String,
  },
  enableCollapse: {
    default: false,
    type: Boolean,
  },
});

const store = useStore();

const emit = defineEmits([
  'update:filter',
  'itemClick',
  'allSelectionButtonClick',
  'granularSelectionButtonClick',
]);

onMounted(() => {
  if (props.readOnly) {
    enableShowItems.value = true;
  }
});

const isContainerOpen = ref(false);
const containerVisible = computed(() => {
  return !props.enableCollapse || isContainerOpen.value;
});
const enableShowItems = ref(false);
const showFilters = ref(false);
const filters = ref({
  textFilter: '',
  countiesFilter: props.availableItems?.counties,
  municipalitiesFilter: props.availableItems?.municipalities,
  unsetFilter: false,
});

const buttonText = computed(() => {
  if (showFilters.value) return '';

  if (props.granularSelectionEnabled && enableShowItems.value) {
    const { singular, plural } = aggregateNaming[props.aggregateLevel];
    const label = props.selectedItems.length === 1 ? singular : plural;
    return `Eingabe für ${label} anwenden`;
  }

  return `Eingabe für alle ${props.buttonNaming} anwenden`;
});

const totalMunicipalities = computed(() => {
  if (!props.availableItems) return null;

  const countiesFilterObj = toObject(filters.value.countiesFilter, 'county_id');
  return props.availableItems?.municipalities.filter((item) => {
    return countiesFilterObj[item.county_id];
  });
});

watch(totalMunicipalities, (newItems, oldItems) => {
  if (!props.availableItems) return;

  if (newItems > oldItems) {
    filters.value.municipalitiesFilter = newItems;
  } else {
    // Make sure that selectedMunicipalities keep the state as it was before,
    // but also remove not anymore available municipalities
    const totalMunisObj = toObject(newItems, 'municipality_id');
    filters.value.municipalitiesFilter =
      filters.value.municipalitiesFilter.filter((item) => {
        return totalMunisObj[item.municipality_id];
      });
  }
});

watch(filters, (newFilters) => emit('update:filter', newFilters), {
  deep: true,
});

function showItems() {
  if (props.granularSelectionEnabled) {
    enableShowItems.value = !enableShowItems.value;
  }

  if (!enableShowItems.value) {
    showFilters.value = false;
  }
}

function handleAllSelectionButtonClick() {
  store.commit('layout/SHOWTOAST', {
    color: 'success',
    message: `Änderungen auf alle ${props.buttonNaming} erfolgreich angewandt`,
    timeout: 2000,
  });
  emit('allSelectionButtonClick');
}
</script>
